import { ref, onMounted, onUnmounted } from 'vue'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'

export function useAdvanceSearchTooltip() {
  const hoverTimer = ref<number | null>(null)
  const activeTooltip = ref<HTMLElement | null>(null)
  const searchIAStore = useSearchIAStore()

  function showTooltip(button: HTMLElement): void {
    trackEvent(TrackerEvents.AI_SEARCH_REFERENCE_OPENED, {
      search_id: searchIAStore.search_id
    })
    // Eliminar tooltips existentes
    hideAllTooltips()

    const tooltipData = decodeURIComponent(button.getAttribute('data-tooltip') || '')

    // Crear el tooltip
    const tooltip = document.createElement('div')
    tooltip.className = 'custom-tooltip tooltip-above'
    tooltip.innerHTML = tooltipData
    document.body.appendChild(tooltip)
    activeTooltip.value = tooltip

    // Posicionar el tooltip
    const rect = button.getBoundingClientRect()
    const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft || document.body.scrollLeft
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Primero añadimos el tooltip al DOM para poder medir su altura
    tooltip.style.visibility = 'hidden'
    tooltip.style.position = 'fixed'
    tooltip.style.top = '0'
    tooltip.style.left = '0'
    tooltip.style.maxWidth = `${viewportWidth * 0.9}px`

    // Ahora que está en el DOM, podemos medir
    setTimeout(() => {
      // Obtener dimensiones del tooltip
      const tooltipHeight = tooltip.offsetHeight
      const tooltipWidth = tooltip.offsetWidth

      // Convertir a posición absoluta para el cálculo final
      tooltip.style.position = 'absolute'

      // Determinar si hay suficiente espacio arriba o abajo
      const elementTop = rect.top
      const elementBottom = rect.bottom
      const spaceAbove = elementTop
      const spaceBelow = viewportHeight - elementBottom

      // Decidir si mostrar arriba o abajo según el espacio disponible
      let topPosition
      if (spaceAbove >= tooltipHeight + 15) {
        // Hay suficiente espacio arriba
        topPosition = rect.top + scrollTop - tooltipHeight - 15
        tooltip.classList.remove('tooltip-below')
        tooltip.classList.add('tooltip-above')
      } else if (spaceBelow >= tooltipHeight + 15) {
        // Hay suficiente espacio abajo
        topPosition = rect.bottom + scrollTop + 15
        tooltip.classList.remove('tooltip-above')
        tooltip.classList.add('tooltip-below')
      } else if (spaceAbove > spaceBelow) {
        // No hay suficiente espacio pero hay más arriba que abajo
        // Mostrar arriba con altura limitada
        topPosition = scrollTop + 15
        tooltip.style.maxHeight = `${spaceAbove - 30}px`
        tooltip.style.overflowY = 'auto'
        tooltip.classList.remove('tooltip-below')
        tooltip.classList.add('tooltip-above')
      } else {
        // No hay suficiente espacio pero hay más abajo que arriba
        // Mostrar abajo con altura limitada
        topPosition = elementBottom + scrollTop + 15
        tooltip.style.maxHeight = `${spaceBelow - 30}px`
        tooltip.style.overflowY = 'auto'
        tooltip.classList.remove('tooltip-above')
        tooltip.classList.add('tooltip-below')
      }

      // Calcular posición horizontal centrada
      const buttonCenterX = rect.left + rect.width / 2
      let leftPosition = buttonCenterX + scrollLeft - tooltipWidth / 2

      // Ajustar si se sale por los bordes
      const minLeftPadding = 10
      const maxRightPadding = 10

      if (leftPosition < scrollLeft + minLeftPadding) {
        // Si se sale por la izquierda
        leftPosition = scrollLeft + minLeftPadding

        // Reposicionar la flecha para que apunte al botón
        const arrowLeft = Math.max(buttonCenterX - leftPosition - minLeftPadding, 10)
        tooltip.style.setProperty('--arrow-left', `${arrowLeft}px`)
      } else if (leftPosition + tooltipWidth > scrollLeft + viewportWidth - maxRightPadding) {
        // Si se sale por la derecha
        leftPosition = scrollLeft + viewportWidth - tooltipWidth - maxRightPadding

        // Reposicionar la flecha para que apunte al botón
        const arrowLeft = Math.min(buttonCenterX - leftPosition, tooltipWidth - 20)
        tooltip.style.setProperty('--arrow-left', `${arrowLeft}px`)
      } else {
        // Centrado normal
        tooltip.style.setProperty('--arrow-left', '50%')
      }

      // Aplicar posiciones
      tooltip.style.top = `${topPosition}px`
      tooltip.style.left = `${leftPosition}px`

      // Asegurar que cualquier tooltip que tenga scroll muestre bien el botón de ir a documento
      const tooltipContent = tooltip.querySelector('.chunk-tooltip')
      if (tooltipContent) {
        tooltipContent.scrollTop = 0
      }

      // Agregar listener para el botón "Ir al documento"
      const goToDocButton = tooltip.querySelector('.go-to-document') as HTMLElement
      if (goToDocButton) {
        goToDocButton.addEventListener('click', (event: Event) => {
          event.preventDefault()
          event.stopPropagation()
          const chunkId = goToDocButton.getAttribute('data-chunk-id')
          if (chunkId) {
            // Usar el store para solicitar abrir el documento
            searchIAStore.requestOpenDocument(chunkId)
            // Ocultar el tooltip después del click
            hideAllTooltips()
          }
        })
      }

      // Hacer visible después de todos los cálculos
      tooltip.style.visibility = 'visible'
    }, 0)
  }

  function hideAllTooltips(): void {
    if (activeTooltip.value) {
      activeTooltip.value.remove()
      activeTooltip.value = null
    }
  }

  function handleMouseOver(event: MouseEvent): void {
    const target = event.target as HTMLElement
    const button = target.closest('.chunk-reference-btn') as HTMLElement

    if (button) {
      // Añadir un pequeño delay para evitar tooltips accidentales
      if (hoverTimer.value) clearTimeout(hoverTimer.value)
      hoverTimer.value = window.setTimeout(() => {
        showTooltip(button)
      }, 300)
    } else if (target.closest('.custom-tooltip')) {
      // Cuando el mouse está sobre el tooltip, cancelar cualquier timer de ocultación
      if (hoverTimer.value) clearTimeout(hoverTimer.value)
    }
  }

  function handleMouseOut(event: MouseEvent): void {
    const target = event.target as HTMLElement
    const button = target.closest('.chunk-reference-btn')
    const isFromTooltip = target.closest('.custom-tooltip')

    if (button) {
      // Cancelar el timer si el mouse sale antes de mostrar el tooltip
      if (hoverTimer.value) clearTimeout(hoverTimer.value)

      // Si el mouse sale del botón al tooltip, no ocultamos
      const relatedTarget = event.relatedTarget as HTMLElement
      if (
        relatedTarget &&
        (relatedTarget.closest('.custom-tooltip') || relatedTarget.closest('.chunk-reference-btn'))
      ) {
        return
      }

      // Pequeño delay antes de ocultar para permitir mover el mouse al tooltip
      hoverTimer.value = window.setTimeout(hideAllTooltips, 300)
    } else if (isFromTooltip) {
      // Si el mouse sale del tooltip
      const relatedTarget = event.relatedTarget as HTMLElement
      if (
        !relatedTarget ||
        (!relatedTarget.closest('.custom-tooltip') && !relatedTarget.closest('.chunk-reference-btn'))
      ) {
        hoverTimer.value = window.setTimeout(hideAllTooltips, 300)
      }
    }
  }

  onMounted(() => {
    document.addEventListener('mouseover', handleMouseOver)
    document.addEventListener('mouseout', handleMouseOut)
  })

  onUnmounted(() => {
    document.removeEventListener('mouseover', handleMouseOver)
    document.removeEventListener('mouseout', handleMouseOut)
    hideAllTooltips()

    if (hoverTimer.value) clearTimeout(hoverTimer.value)
  })

  return {
    activeTooltip,
    hoverTimer,
    showTooltip,
    hideAllTooltips
  }
}
